import axios from 'axios';
import type { FormSubmission, APIResponse } from '@/types';
import { WEBHOOK_URL, REDIRECT_BASE_URL } from '@config/constants';

const API_TIMEOUT = 30000; // 30 seconds
const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 seconds

export class APIService {
  private static backupQueue: FormSubmission[] = [];

  /**
   * Submit form data to webhook
   */
  static async submitForm(data: FormSubmission): Promise<APIResponse> {
    try {
      console.log('📤 Submitting form data:', data);
      const response = await this.sendWithRetry(data);

      // If success, try to send backups
      await this.processBackupQueue();

      return response;
    } catch (error) {
      // Save for retry later
      this.addToBackupQueue(data);
      throw this.handleError(error);
    }
  }

  /**
   * Send with retry logic
   */
  private static async sendWithRetry(data: FormSubmission, attempt = 1): Promise<APIResponse> {
    try {
      const response = await axios.post<APIResponse>(WEBHOOK_URL, data, {
        headers: {
          'Content-Type': 'application/json',
          'X-Form-Version': '1.0.0',
        },
        timeout: API_TIMEOUT,
        validateStatus: status => status < 500, // Retry only for server errors
      });

      if (response.status >= 400) {
        throw new Error(`API Error: ${response.status} - ${response.statusText}`);
      }

      return response.data;
    } catch (error) {
      if (attempt < MAX_RETRIES && this.shouldRetry(error)) {
        console.log(`🔄 Attempt ${attempt}/${MAX_RETRIES} failed, retrying...`);
        await this.delay(RETRY_DELAY * attempt);
        return this.sendWithRetry(data, attempt + 1);
      }
      throw error;
    }
  }

  /**
   * Check if error is retryable
   */
  private static shouldRetry(error: unknown): boolean {
    if (!axios.isAxiosError(error)) return false;

    // Retry for network errors or server errors 5xx
    return !error.response || error.response.status >= 500;
  }

  /**
   * Handle errors
   */
  private static handleError(error: unknown): Error {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        // Server responded with error
        return new Error(
          `Erreur serveur: ${error.response.status} - ${
            error.response.data?.message || error.message
          }`
        );
      } else if (error.request) {
        // No response received
        return new Error('Impossible de contacter le serveur. Vérifiez votre connexion.');
      }
    }

    return new Error("Une erreur inattendue s'est produite.");
  }

  /**
   * Add to backup queue
   */
  private static addToBackupQueue(data: FormSubmission): void {
    this.backupQueue.push(data);

    // Save to localStorage
    localStorage.setItem('pemlab_backup_queue', JSON.stringify(this.backupQueue));
    console.log('💾 Form data saved to backup queue');
  }

  /**
   * Process backup queue
   */
  private static async processBackupQueue(): Promise<void> {
    const saved = localStorage.getItem('pemlab_backup_queue');
    if (saved) {
      try {
        this.backupQueue = JSON.parse(saved);
      } catch (error) {
        console.error('Error parsing backup queue:', error);
        return;
      }
    }

    if (this.backupQueue.length === 0) return;

    console.log(`📤 Processing ${this.backupQueue.length} backups...`);
    const remaining: FormSubmission[] = [];

    for (const submission of this.backupQueue) {
      try {
        await this.sendWithRetry(submission);
        console.log('✅ Backup sent successfully:', submission.client.email);
      } catch (error) {
        console.error('❌ Failed to send backup:', error);
        remaining.push(submission);
      }
    }

    this.backupQueue = remaining;

    if (remaining.length > 0) {
      localStorage.setItem('pemlab_backup_queue', JSON.stringify(remaining));
      console.log(`⚠️ ${remaining.length} backups still pending`);
    } else {
      localStorage.removeItem('pemlab_backup_queue');
      console.log('✅ All backups sent successfully');
    }
  }

  /**
   * Utility delay function
   */
  private static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Build redirect URL with products
   */
  static getRedirectUrl(products: string[]): string {
    const productRefs = products.join('+');
    return `${REDIRECT_BASE_URL}${productRefs}`;
  }

  /**
   * Build form submission payload
   */
  static buildFormSubmission(
    clientInfo: ClientInfo,
    products: string[],
    bonusProducts: string[],
    totalPoints: number,
    answers: Map<string, Answer>,
    questions: Question[],
    resultLinks?: { pageUrl: string; mailUrl: string }
  ): FormSubmission {
    // Build questions/answers array
    const questionsReponses = Array.from(answers.entries()).map(([questionId, answer]) => {
      const question = questions.find(q => q.id === questionId);
      return {
        code: answer.values.join(','),
        question: question?.title || questionId,
        reponse: answer.values.join(', '),
        points: answer.points,
      };
    });

    // Get all selected codes
    const codesSelectionnes = Array.from(answers.values()).flatMap(answer => answer.values);

    return {
      client: clientInfo,
      produits: products.filter(p => !bonusProducts.includes(p)),
      produitsBonus: bonusProducts,
      totalPoints,
      questionsReponses,
      codesSelectionnes,
      timestamp: new Date().toISOString(),
      ...(resultLinks && {
        resultPageUrl: resultLinks.pageUrl,
        resultMailUrl: resultLinks.mailUrl
      })
    };
  }
}

// Type imports for buildFormSubmission
import type { ClientInfo, Answer, Question } from '@/types';
